import {
  type MaybeRef,
  computed,
  getCurrentInstance,
  inject,
  ref,
  shallowRef,
  toValue
} from 'vue'
import type { Stakeholder, StakeholderType } from '@gat-ship/types'
import {
  appLoadingKey,
  stakeholderKey,
  type StakeholderCommands,
  type StakeholderService
} from '@gat-ship/types/injection-keys'
import { globalUtils } from '@gat-ship/utils'
import { useStakeholderSignalREvents } from '@gat-ship/signalr'
import { useFieldConfigurationApiCalls } from '@gat-ship/custom-fields'
import { useStakeholderApiCalls } from './useStakeholderApiCalls'
import { stakeholderTypeToTarget } from '@gat-ship/stakeholders'

/**
 * Single-stakeholder detail datasource. Owns the loaded stakeholder, its
 * custom-field values, the SignalR subscriptions that keep it fresh, and the
 * `commands` namespace that performs every mutation (loading toggle →
 * command-id → API → apply `newState`). Constructed once per
 * `StakeholderProvider` and provided under `stakeholderKey`.
 */
export function useStakeholder(
  stakeholderType: MaybeRef<StakeholderType | undefined>
): StakeholderService {
  const api = useStakeholderApiCalls()
  const { getCustomFieldValuesRequest, patchCustomFieldValuesRequest } =
    useFieldConfigurationApiCalls()
  // Self-inject the app-loading registry so command spinners work without
  // importing the UI package. Guarded for non-component callers (unit tests).
  const loading = getCurrentInstance()
    ? inject(appLoadingKey, undefined)
    : undefined

  const _stakeholder = shallowRef<Stakeholder>()
  const _customFieldValues = ref<Record<string, unknown>>({})
  const _isLoading = ref(false)

  function _setStakeholder(data: Stakeholder) {
    _stakeholder.value = data
  }

  function _setCustomFieldValues(values: Record<string, unknown>) {
    _customFieldValues.value = values
  }

  async function fetchStakeholder(
    id: string,
    options?: { forceFetch?: boolean }
  ) {
    const foundStakeholder = _stakeholder.value?.id === id
    if (foundStakeholder && !options?.forceFetch) {
      return
    }
    _isLoading.value = true
    try {
      const result = await api.getStakeholderRequest({ stakeholderId: id })
      _setStakeholder(result.stakeholder)
    } finally {
      _isLoading.value = false
    }
    fetchCustomFieldValues(id)
  }

  async function fetchCustomFieldValues(id: string) {
    // Prefer the loaded stakeholder's real type over the (possibly defaulted)
    // input hint so a bare `?stakeholder=<id>` deep link fetches values against
    // the correct entity target instead of the client default.
    const _type =
      _stakeholder.value?.stakeholderType ?? toValue(stakeholderType)
    if (!_type) return
    const target = stakeholderTypeToTarget[_type]
    try {
      const result = await getCustomFieldValuesRequest(target, id)
      _customFieldValues.value = result.fieldValues
    } catch {
      _customFieldValues.value = {}
    }
  }

  /** Run a write that returns the stakeholder's new state, toggling loading. */
  async function runWrite(
    id: string,
    write: (commandId: string) => Promise<{ newState: Stakeholder }>
  ) {
    loading?.setLoadingId(id)
    try {
      const result = await write(globalUtils.functions.uuid())
      if (result?.newState) {
        _setStakeholder(result.newState)
      }
    } finally {
      loading?.removeLoadingId(id)
    }
  }

  const commands: StakeholderCommands = {
    changeGeneralInfo: async (id, payload, customFieldValues) => {
      loading?.setLoadingId(id)
      try {
        const commandId = globalUtils.functions.uuid()
        const response = await api.changeStakeholderInfoRequest({
          id,
          payload,
          commandId
        })
        _setStakeholder(response.newState)
        // When the type has custom fields, patch the edited values in the
        // same write window (mirrors the previous modal behaviour).
        if (customFieldValues !== undefined) {
          const target =
            stakeholderTypeToTarget[response.newState.stakeholderType]
          const patchResult = await patchCustomFieldValuesRequest(
            target,
            id,
            { fieldValues: customFieldValues },
            commandId
          )
          _setCustomFieldValues(patchResult.fieldValues)
        }
      } finally {
        loading?.removeLoadingId(id)
      }
    },
    changeInternalIdentificationNumbers: (id, payload) =>
      runWrite(id, (commandId) =>
        api.changeStakeholderInternalIdentificationNumbersRequest({
          id,
          payload,
          commandId
        })
      ),
    changeOfficialBusinessNumbers: (id, payload) =>
      runWrite(id, (commandId) =>
        api.changeStakeholderBusinessNumbersRequest({ id, payload, commandId })
      ),
    changePostalAddress: (id, payload) =>
      runWrite(id, (commandId) =>
        api.changeStakeholderPostalAddressRequest({ id, payload, commandId })
      ),
    changeVisitAddress: (id, payload) =>
      runWrite(id, (commandId) =>
        api.changeStakeholderVisitAddressRequest({ id, payload, commandId })
      ),
    changeBillingDetails: (id, payload) =>
      runWrite(id, (commandId) =>
        api.changeStakeholderBillingDetailsRequest({ id, payload, commandId })
      ),
    remove: (id) =>
      runWrite(id, (commandId) =>
        api.removeStakeholderRequest({ id, commandId })
      ),
    restore: (id) =>
      runWrite(id, (commandId) =>
        api.restoreStakeholderRequest({ id, commandId })
      )
  }

  const signalR = useStakeholderSignalREvents({
    onStakeholderAdded: (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    },
    onStakeholderInternalIdentificationNumbersChanged: (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    },
    onStakeholderOfficialBusinessNumbersChanged: (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    },
    onStakeholderVisitAddressChanged: async (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    },
    onStakeholderPostalAddressChanged: async (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    },
    onStakeholderGeneralInfoChanged: (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    },
    onStakeholderRemoved: (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    },
    onStakeholderRestored: (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    },
    onStakeholderBillingDetailsChanged: (payload) => {
      if (payload.message.stakeholderId !== _stakeholder.value?.id) {
        return
      }
      fetchStakeholder(payload.message.stakeholderId, { forceFetch: true })
    }
  })

  function subscribeToStakeholderEvents() {
    try {
      signalR.subscribeToStakeholderEvents()
    } catch (error) {
      console.error('Error subscribing to stakeholder events', error)
    }
  }

  function unsubscribeFromStakeholderEvents() {
    signalR.unsubscribeFromStakeholderEvents()
  }

  return {
    stakeholder: computed(() => _stakeholder.value),
    customFieldValues: computed(() => _customFieldValues.value),
    isLoading: computed(() => _isLoading.value),
    setStakeholder: _setStakeholder,
    setCustomFieldValues: _setCustomFieldValues,
    fetchStakeholder,
    fetchCustomFieldValues,
    subscribeToStakeholderEvents,
    unsubscribeFromStakeholderEvents,
    commands
  }
}

/** Inject the stakeholder detail service provided by `StakeholderProvider`. */
export function useStakeholderContext() {
  return inject(stakeholderKey)
}
